import React, { ReactNode, useEffect, useRef } from "react";
import { createContext, useState } from "react";
import { User } from "@/type/user";
import GraphConfigurationModal from "@/components/pages/edit/GraphConfigurationModal";
import { GraphConfiguration, Model, Page, Category } from "@/type/common";
import { osolutionTokenManager, tokenManager } from "@/services/axios";
import axios from "@/services/axios";
import { refactor_models } from "@/utils/refactor";
import { GraphConfigProvider } from "./GraphConfigContext";
import { JWT_PUBLIC } from "@/data/public";
import { verifyToken } from "@/lib/jwt";
import ErrorPage from "@/pages/ErrorPage";
import ConfirmationModal, {
  ConfirmFunction,
} from "@/components/common/ConfirmationModal";
import SkeletonLoader from "@/components/common/SkeletonLoader";
import { SidebarProvider } from "@/components/ui/sidebar";
import { languages } from "@/i18n/config";
import { useTranslation } from "react-i18next";

const AUTH_CACHE_KEY = "auth_cache";

const default_user: User = {
  plan: "unloaded",
};

// Helper functions for cache
function getAuthCache() {
  try {
    const raw = localStorage.getItem(AUTH_CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}
function setAuthCache(data: any) {
  try {
    localStorage.setItem(AUTH_CACHE_KEY, JSON.stringify(data));
  } catch {}
}

type AuthContextType = {
  user: User;
  isEditor: boolean;
  isPdf: boolean;
  pages: Page[];
  setPages: (pages: any) => void;
  categories: Category[];
  configs: GraphConfiguration[];
  setConfigs: (configs: any) => void;
  openGraphConfiguration: (data: any) => void;
  models: any[];
  osolutionAuthData: any;
  editMode: boolean;
  setEditMode: (editMode: boolean) => void;
  openConfirm: (
    title: string,
    description: string,
    confirm: ConfirmFunction,
    dismiss: ConfirmFunction,
    confirmationText: string
  ) => void;
  loadPages: () => Promise<any>;
  loadConfigs: () => Promise<any>;
  loadCategories: () => Promise<any>;
};

const initialAuthContextValue: AuthContextType = {
  user: { ...default_user },
  isEditor: false,
  isPdf: false,
  pages: [],
  setPages: () => null,
  categories: [],
  configs: [],
  setConfigs: () => null,
  openGraphConfiguration: () => null,
  models: [],
  osolutionAuthData: null,
  editMode: false,
  setEditMode: () => null,
  openConfirm: () => null,
  loadPages: async () => {},
  loadConfigs: async () => {},
  loadCategories: async () => {},
};

const AuthContext = createContext(initialAuthContextValue);

type AuthProviderProps = {
  children: ReactNode;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { i18n } = useTranslation();
  const graphOpen = useRef<any>((_data: any) => null);
  const [user, setUser] = useState({ ...default_user });
  const [isEditor, setIsEditor] = useState(false);
  const [models, setModels] = useState<Model[]>([]);
  const [pages, setPages] = useState<Page[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [configs, setConfigs] = useState<GraphConfiguration[]>([]);
  const [loaded, setLoaded] = useState<boolean | null>(false);
  const [isPdf, setIsPdf] = useState(false);
  const osolutionAuthData = useRef({
    url: "",
    token: "",
  });
  const [editMode, setEditMode] = useState(false);
  const openConfirmRef = useRef(
    (
      _title: string,
      _description: string,
      _confirm: ConfirmFunction,
      _dismiss: ConfirmFunction,
      _confirmationText: string
    ) => {}
  );
  const [offline, setOffline] = useState(false);

  const openConfirm = (
    title: string,
    description: string,
    confirm: ConfirmFunction,
    dismiss: ConfirmFunction,
    confirmationText: string
  ) => {
    openConfirmRef.current(
      title,
      description,
      confirm,
      dismiss,
      confirmationText
    );
  };

  // Modified: allow passing initial values
  const loadPages = async () => {
    const page_response = await axios
      .get("/odash/pages")
      .then((e) => e.data)
      .catch((_e) => null);
    if (page_response) {
      setPages(page_response);
    }
    return page_response;
  };

  const loadConfigs = async () => {
    const config_response = await axios
      .get("/odash/data")
      .then((e) => e.data)
      .catch((_e) => null);
    if (config_response) {
      setConfigs(config_response);
    }
    return config_response;
  };

  const loadCategories = async () => {
    const categories_response = await axios
      .get("/odash/categories")
      .then((e) => e.data)
      .catch((_e) => null);
    if (categories_response) {
      setCategories(categories_response);
    }
    return categories_response;
  };

  const loadToken = async () => {
    const response = await axios
      .get("/odash/access")
      .then((e) => e.data)
      .catch(() => null);

    if (response) {
      // Handle both old format (string) and new format (object with token and license_key)
      const token = typeof response === 'string' ? response : response.token;
      const licenseKey = typeof response === 'object' ? response.license_key : null;
      
      // Store license key in localStorage for later use
      if (licenseKey) {
        localStorage.setItem('odash_license_key', licenseKey);
      }
      
      const verified = await verifyToken(token, JWT_PUBLIC);
      osolutionTokenManager.setTokens({
        accessToken: token,
        refreshToken: token,
      });
      setUser({
        ...default_user,
        plan: verified ? (verified.plan as string) : "",
      });

      if (verified && verified.plan === "freemium") {
        setTimeout(() => {
          setPages((pages) =>
            pages.map((page, index) => ({
              ...page,
              content: index !== 0 ? [] : page.content,
            }))
          );
        }, 500);
      }
      return verified;
    }
    return false;
  };

  // New: background refresh
  const refreshAll = async () => {
    const [page_response, config_response, categories_response, token] = await Promise.all([
      loadPages(),
      loadConfigs(),
      loadCategories(),
      loadToken(),
    ]);
    if (token !== false) {
      setAuthCache({ pages: page_response, configs: config_response, categories: categories_response, token });
    }
    return token;
  };

  const reloadFull = async (company_ids: number[]) => {
    await axios.post("/odash/refresh-dashboard", { company_ids });
    localStorage.clear();
    window.parent.postMessage({ type: "refresh" }, "*");
  };

  // Modified setup logic
  useEffect(() => {
    let searchParams = new URLSearchParams(window.location.search);

    if (!searchParams.get("token")) {
      if (localStorage.getItem("url")) {
        const url = new URLSearchParams(localStorage.getItem("url") || "");
        searchParams = url;
      } else {
        setOffline(true);
        return;
      }
    } else {
      localStorage.setItem("url", searchParams.toString());
    }

    const tokenUrl = searchParams.get("token");

    const pdf = searchParams.get("is_pdf");
    let lang = (searchParams.get("lang") || "en").split("-")[0];
    const defaultLang = "en";

    if (!languages.find((l) => l.code === lang)) {
      lang = defaultLang;
    }

    i18n.changeLanguage(lang);

    setIsPdf(pdf === "true");
    let [token, url, key, user_id, partner_id, mode, company_ids] =
      tokenUrl?.split("|") || [];

    const companyIds = {
      python: company_ids
        .split(",")
        .map((e) => parseInt(e))
        .sort((a, b) => a - b)
        .join(","),
      js: (searchParams.get("company_ids") || "")
        .split(",")
        .map((e) => parseInt(e))
        .sort((a, b) => a - b)
        .join(","),
    };

    if (mode === "editor") {
      setIsEditor(true);
    }

    localStorage.setItem("user_id", user_id);
    localStorage.setItem("partner_id", partner_id);

    url = decodeURIComponent(url);

    tokenManager.setBaseURL(url);
    osolutionAuthData.current = { url, token };
    tokenManager.setTokens({
      accessToken: token,
      refreshToken: token,
    });
    localStorage.setItem("state-manager-key", key);

    if (
      companyIds.js !== companyIds.python &&
      !window.location.href.includes("/public")
    ) {
      reloadFull(
        (searchParams.get("company_ids") || "")
          .split(",")
          .map((e) => parseInt(e))
      );
      return;
    }

    const storedCompanies = localStorage.getItem("companies");
    if (storedCompanies && storedCompanies !== companyIds.python) {
      localStorage.removeItem("data-cache");
    }
    localStorage.setItem("companies", companyIds.python);

    setModels([]);
    const cache = getAuthCache();
    if (cache && cache.pages && cache.configs && cache.token) {
      setPages(cache.pages);
      setConfigs(cache.configs);
      if (cache.categories) {
        setCategories(cache.categories);
      }
      setLoaded(true);
      refreshAll().then((token) => {
        if (token === false) {
          setLoaded(null);
        } else {
          axios
            .post("/odash/execute", { action: "get_models", parameters: {} })
            .then((e) => e.data)
            .then((ret) => {
              if (ret) setModels(refactor_models(ret));
            });
        }
      });
    } else {
      (async () => {
        await loadPages();
        await loadConfigs();
        await loadCategories();
        const token = await loadToken();
        if (token === false) {
          setLoaded(null);
          return;
        }
        setLoaded(true);
        setAuthCache({ pages, configs, categories, token });
        const ret = await axios
          .post("/odash/execute", { action: "get_models", parameters: {} })
          .then((e) => e.data)
          .catch((_e) => null);
        if (ret) {
          setModels(refactor_models(ret));
        }
      })();
    }
  }, []);

  const openGraphConfiguration = (data: any) => {
    graphOpen.current(data);
  };

  if (offline) {
    window.location.href = "https://portal.osolutions.app";
    return null;
  }

  if (loaded === false) {
    return (
      <SidebarProvider>
        <SkeletonLoader />
      </SidebarProvider>
    );
  } else if (loaded === null) {
    return <ErrorPage />;
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isEditor,
        isPdf,
        pages,
        setPages,
        categories,
        configs,
        setConfigs,
        openGraphConfiguration,
        models,
        osolutionAuthData,
        editMode,
        setEditMode,
        openConfirm,
        loadPages,
        loadConfigs,
        loadCategories,
      }}
    >
      <GraphConfigProvider>
        <GraphConfigurationModal open={graphOpen} />
        {children}
      </GraphConfigProvider>
      <ConfirmationModal openConfirmRef={openConfirmRef} />
    </AuthContext.Provider>
  );
};

export default AuthContext;
